import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, TrashSimple, MagnifyingGlass, Copy, FunnelSimple, DownloadSimple, PencilSimple, Paperclip, Printer, Folders } from "@phosphor-icons/react";
import { Modal, ModalFooter, Fld } from "./TCAndTCSR";
import { printFilterSummary } from "@/lib/printSummary";
import { MultiFilePicker } from "@/components/MultiFilePicker";

function fmtDate(s){ if(!s) return "—"; if(/^\d{4}-\d{2}-\d{2}$/.test(s)) return s; const d=new Date(s); if(Number.isNaN(d.getTime())) return s; return d.toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"});}

export function MiscRecordPage({
  variant, kicker, title, subtitle,
  extraHeaders = {}, passcodeQuery = "",
  testIdPrefix,
  Icon = Folders,
}) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const [q, setQ] = useState(params.get("q") || "");
  const [from, setFrom] = useState(params.get("from") || "");
  const [to, setTo] = useState(params.get("to") || "");
  const [sortKey, setSortKey] = useState(params.get("sort") || "date");
  const [sortDir, setSortDir] = useState(params.get("dir") === "asc" ? "asc" : "desc");

  useEffect(() => {
    const n = {};
    if (q) n.q = q; if (from) n.from = from; if (to) n.to = to;
    if (sortKey) { n.sort = sortKey; n.dir = sortDir; }
    setParams(n, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, from, to, sortKey, sortDir]);

  const cfg = { headers: extraHeaders };
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/misc-records/${variant}`, cfg); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [variant, JSON.stringify(extraHeaders)]);

  const del = async (r) => {
    if (!window.confirm(`Delete "${r.title || r.name}" from ${fmtDate(r.date)}?`)) return;
    try { await api.delete(`/misc-records/${variant}/${r.id}`, cfg); setItems(xs => xs.filter(x => x.id !== r.id)); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const openFile = (id) => {
    const t = localStorage.getItem("access_token") || "";
    const pc = passcodeQuery ? `&passcode=${encodeURIComponent(passcodeQuery)}` : "";
    window.open(`${API}/misc-records/${variant}/${id}/download?auth=${encodeURIComponent(t)}${pc}`, "_blank");
  };
  const shareLink = async () => {
    const url = window.location.href;
    try { await navigator.clipboard.writeText(url); toast.success("Filtered link copied"); return; } catch {}
    try {
      const ta = document.createElement("textarea"); ta.value = url; ta.style.position="fixed"; ta.style.opacity="0";
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      toast.success("Filtered link copied");
    } catch { toast.error("Could not copy — copy the URL manually"); }
  };
  const clearF = () => { setQ(""); setFrom(""); setTo(""); setSortKey("date"); setSortDir("desc"); };
  const toggleSort = (k) => {
    if (sortKey !== k) { setSortKey(k); setSortDir("asc"); }
    else if (sortDir === "asc") setSortDir("desc");
    else { setSortKey(null); setSortDir("asc"); }
  };

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    return items.filter(x => {
      if (n) {
        const hay = `${x.name||""} ${x.title||""} ${x.origin||""} ${x.destination||""} ${x.filename||""}`.toLowerCase();
        if (!hay.includes(n)) return false;
      }
      if (from && (x.date||"") && x.date < from) return false;
      if (to   && (x.date||"") && x.date > to)   return false;
      return true;
    });
  }, [items, q, from, to]);

  const displayed = useMemo(() => {
    if (!sortKey) return filtered;
    const dir = sortDir === "asc" ? 1 : -1;
    const g = (x) => ({
      date: x.date||"",
      name: (x.name||"").toLowerCase(),
      origin: (x.origin||"").toLowerCase(),
      destination: (x.destination||"").toLowerCase(),
      title: (x.title||"").toLowerCase(),
    }[sortKey] ?? "");
    return [...filtered].sort((a,b)=>{ const va=g(a), vb=g(b); if (va<vb) return -dir; if (va>vb) return dir; return 0; });
  }, [filtered, sortKey, sortDir]);

  const hasF = q || from || to;

  const onPrint = () => {
    printFilterSummary({
      title: `${title} — Filter Summary`,
      filters: [
        q && `Search: "${q}"`,
        from && `From: ${from}`,
        to && `To: ${to}`,
      ].filter(Boolean),
      columns: [
        { key: "date",        label: "Date",        format: fmtDate },
        { key: "name",        label: "Company Name" },
        { key: "origin",      label: "Origin" },
        { key: "destination", label: "Destination" },
        { key: "title",       label: "Title" },
        { key: "filename",    label: "File" },
      ],
      rows: displayed,
    });
  };

  return (
    <Layout>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-2">{kicker}</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-zinc-950">{title}</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onPrint} data-testid={`${testIdPrefix}-print-btn`} className="bg-white border border-zinc-200 text-zinc-800 text-sm px-3 py-2.5 flex items-center gap-2 hover:bg-zinc-50 transition-colors duration-150"><Printer size={14}/> Print filter summary</button>
          <button onClick={shareLink} data-testid={`${testIdPrefix}-share-btn`} className="bg-white border border-zinc-200 text-zinc-800 text-sm px-3 py-2.5 flex items-center gap-2 hover:bg-zinc-50 transition-colors duration-150"><Copy size={14}/> Copy share link</button>
          <button onClick={()=>setShowNew(true)} data-testid={`new-${testIdPrefix}-btn`} className="bg-[#002FA7] text-white text-sm px-4 py-2.5 flex items-center gap-2 hover:bg-[#00227A] transition-colors duration-150"><Plus size={16}/> New Record</button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 p-3 mb-4 grid grid-cols-1 md:grid-cols-4 gap-2" data-testid={`${testIdPrefix}-filters`}>
        <div className="md:col-span-2 relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search company name, title, origin, destination, file…" data-testid={`${testIdPrefix}-search`} className="w-full text-sm border border-zinc-200 pl-9 pr-3 py-2 focus:outline-none focus:border-[#002FA7]"/>
        </div>
        <input type="date" value={from} onChange={e=>setFrom(e.target.value)} data-testid={`${testIdPrefix}-from`} className="font-mono text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]"/>
        <input type="date" value={to} onChange={e=>setTo(e.target.value)} data-testid={`${testIdPrefix}-to`} className="font-mono text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]"/>
      </div>

      {hasF && (
        <div className="text-xs text-zinc-500 font-mono mb-3 flex items-center gap-3">
          <FunnelSimple size={12}/> Showing {displayed.length} of {items.length}
          <button onClick={clearF} className="text-[#002FA7] hover:underline">Clear filters</button>
        </div>
      )}

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[1050px]">
          <thead>
            <tr className="border-b border-zinc-200 text-[10px] uppercase font-mono text-zinc-500 bg-zinc-50">
              <SortTh label="Date"        k="date"        sortKey={sortKey} dir={sortDir} onClick={()=>toggleSort("date")}/>
              <SortTh label="Company Name" k="name"        sortKey={sortKey} dir={sortDir} onClick={()=>toggleSort("name")}/>
              <SortTh label="Origin"      k="origin"      sortKey={sortKey} dir={sortDir} onClick={()=>toggleSort("origin")}/>
              <SortTh label="Destination" k="destination" sortKey={sortKey} dir={sortDir} onClick={()=>toggleSort("destination")}/>
              <SortTh label="Title"       k="title"       sortKey={sortKey} dir={sortDir} onClick={()=>toggleSort("title")}/>
              <th className="text-left px-4 py-3">File</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="text-center py-16 text-zinc-500 font-mono text-sm">Loading…</td></tr>
            : displayed.length === 0 ? <tr><td colSpan={7} className="text-center py-16 text-zinc-500"><Icon size={40} weight="duotone" className="mx-auto text-zinc-300 mb-3"/><div className="font-mono text-sm">{items.length===0?"No records yet — add the first one.":"No records match these filters."}</div></td></tr>
            : displayed.map(r => (
              <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-150" data-testid={`${testIdPrefix}-row-${r.id}`}>
                <td className="px-4 py-3 font-mono text-zinc-700 whitespace-nowrap">{fmtDate(r.date)}</td>
                <td className="px-4 py-3 text-zinc-900 font-medium truncate max-w-[200px]">{r.name}</td>
                <td className="px-4 py-3 text-zinc-700 truncate max-w-[160px]">{r.origin || <span className="text-zinc-400">—</span>}</td>
                <td className="px-4 py-3 text-zinc-700 truncate max-w-[160px]">{r.destination || <span className="text-zinc-400">—</span>}</td>
                <td className="px-4 py-3 text-zinc-800 truncate max-w-[220px]" title={r.title}>{r.title || <span className="text-zinc-400">—</span>}</td>
                <td className="px-4 py-3">
                  {r.filename ? (
                    <button onClick={()=>openFile(r.id)} className="inline-flex items-center gap-1 text-[#002FA7] hover:underline text-sm" data-testid={`${testIdPrefix}-view-${r.id}`}>
                      <Paperclip size={14} weight="duotone"/> <span className="truncate max-w-[160px]">{r.filename}</span>
                    </button>
                  ) : <span className="text-zinc-400 font-mono text-xs">—</span>}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {r.filename && (<button onClick={()=>openFile(r.id)} data-testid={`${testIdPrefix}-download-${r.id}`} className="inline-flex items-center gap-1 text-xs text-[#002FA7] px-2 py-1 hover:bg-blue-50 transition-colors duration-150"><DownloadSimple size={14}/> File</button>)}
                  <button onClick={()=>setEditing(r)} data-testid={`${testIdPrefix}-edit-${r.id}`} className="inline-flex items-center gap-1 text-xs text-[#002FA7] px-2 py-1 hover:bg-blue-50 transition-colors duration-150 ml-1"><PencilSimple size={14}/> Edit</button>
                  {(user?.role === "admin" || r.created_by_id === user?.id) && (<button onClick={()=>del(r)} data-testid={`${testIdPrefix}-delete-${r.id}`} className="inline-flex items-center gap-1 text-xs text-red-700 px-2 py-1 hover:bg-red-50 transition-colors duration-150 ml-1"><TrashSimple size={14}/> Delete</button>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && <NewMiscModal variant={variant} extraHeaders={extraHeaders} testIdPrefix={testIdPrefix} onClose={()=>setShowNew(false)} onCreated={rs=>{ setItems(xs=>[...rs,...xs]); setShowNew(false); }}/>}
      {editing && <EditMiscModal variant={variant} extraHeaders={extraHeaders} testIdPrefix={testIdPrefix} record={editing} onClose={()=>setEditing(null)} onSaved={u=>{ setItems(xs=>xs.map(x=>x.id===u.id?{...x,...u}:x)); setEditing(null); }}/>}
    </Layout>
  );
}

function SortTh({ label, k, sortKey, dir, onClick }) {
  const active = sortKey === k;
  const arrow = !active ? "↕" : dir === "asc" ? "↑" : "↓";
  return <th className="text-left px-4 py-3 select-none">
    <button type="button" onClick={onClick} data-testid={`sort-${k}`} className={`inline-flex items-center gap-1 uppercase tracking-[0.05em] hover:text-zinc-900 transition-colors duration-150 ${active?"text-zinc-900":"text-zinc-500"}`}>
      {label}<span className={`font-mono text-[11px] ${active?"text-[#002FA7]":"text-zinc-400"}`}>{arrow}</span>
    </button>
  </th>;
}

function NewMiscModal({ variant, extraHeaders, testIdPrefix, onClose, onCreated }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [name, setName] = useState("");
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [title, setTitle] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const created = [];
      const rounds = files.length || 1;
      for (let i = 0; i < rounds; i++) {
        const fd = new FormData();
        fd.append("date", date);
        fd.append("name", name.trim());
        fd.append("origin", origin.trim());
        fd.append("destination", destination.trim());
        fd.append("title", title.trim());
        if (files[i]) fd.append("file", files[i]);
        const { data } = await api.post(`/misc-records/${variant}`, fd, { headers: { "Content-Type": "multipart/form-data", ...extraHeaders }});
        created.push(data);
      }
      toast.success(created.length === 1 ? "Record added" : `${created.length} records added`);
      onCreated(created);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return <Modal title="New Record" testid={`new-${testIdPrefix}-modal`} onClose={onClose}>
    <form onSubmit={submit} className="p-5 space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Fld label="Date" required><input type="date" value={date} onChange={e=>setDate(e.target.value)} data-testid={`${testIdPrefix}-date-input`} className="w-full font-mono text-sm border border-zinc-200 px-3 py-2"/></Fld>
        <Fld label="Company Name" required><input value={name} onChange={e=>setName(e.target.value)} data-testid={`${testIdPrefix}-name-input`} placeholder="Customer or vendor company" className="w-full text-sm border border-zinc-200 px-3 py-2"/></Fld>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Fld label="Origin"><input value={origin} onChange={e=>setOrigin(e.target.value)} data-testid={`${testIdPrefix}-origin-input`} placeholder="e.g. KUL" className="w-full text-sm border border-zinc-200 px-3 py-2"/></Fld>
        <Fld label="Destination"><input value={destination} onChange={e=>setDestination(e.target.value)} data-testid={`${testIdPrefix}-dest-input`} placeholder="e.g. SIN" className="w-full text-sm border border-zinc-200 px-3 py-2"/></Fld>
      </div>
      <Fld label="Title"><input value={title} onChange={e=>setTitle(e.target.value)} data-testid={`${testIdPrefix}-title-input`} placeholder="Short description" className="w-full text-sm border border-zinc-200 px-3 py-2"/></Fld>
      <Fld label="Attachments (any file type — creates one record per file)">
        <MultiFilePicker files={files} onChange={setFiles} testid={`${testIdPrefix}-file`} hint="PDF, Excel, ZIP, CSV, Word, images…"/>
      </Fld>
      <ModalFooter onClose={onClose} saving={saving} submitTestId={`${testIdPrefix}-submit-btn`}/>
    </form>
  </Modal>;
}

function EditMiscModal({ variant, extraHeaders, testIdPrefix, record, onClose, onSaved }) {
  const [date, setDate] = useState(record.date || "");
  const [name, setName] = useState(record.name || "");
  const [origin, setOrigin] = useState(record.origin || "");
  const [destination, setDestination] = useState(record.destination || "");
  const [title, setTitle] = useState(record.title || "");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch(`/misc-records/${variant}/${record.id}`, {
        date, name: name.trim(), origin: origin.trim(), destination: destination.trim(), title: title.trim(),
      }, { headers: extraHeaders });
      toast.success("Saved"); onSaved(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return <Modal title="Edit Record" testid={`edit-${testIdPrefix}-modal`} onClose={onClose}>
    <form onSubmit={submit} className="p-5 space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Fld label="Date"><input type="date" value={date} onChange={e=>setDate(e.target.value)} data-testid={`${testIdPrefix}-edit-date`} className="w-full font-mono text-sm border border-zinc-200 px-3 py-2"/></Fld>
        <Fld label="Company Name"><input value={name} onChange={e=>setName(e.target.value)} data-testid={`${testIdPrefix}-edit-name`} className="w-full text-sm border border-zinc-200 px-3 py-2"/></Fld>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Fld label="Origin"><input value={origin} onChange={e=>setOrigin(e.target.value)} data-testid={`${testIdPrefix}-edit-origin`} className="w-full text-sm border border-zinc-200 px-3 py-2"/></Fld>
        <Fld label="Destination"><input value={destination} onChange={e=>setDestination(e.target.value)} data-testid={`${testIdPrefix}-edit-dest`} className="w-full text-sm border border-zinc-200 px-3 py-2"/></Fld>
      </div>
      <Fld label="Title"><input value={title} onChange={e=>setTitle(e.target.value)} data-testid={`${testIdPrefix}-edit-title`} className="w-full text-sm border border-zinc-200 px-3 py-2"/></Fld>
      <ModalFooter onClose={onClose} saving={saving} submitTestId={`${testIdPrefix}-edit-save`}/>
    </form>
  </Modal>;
}
